import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import feesAndPaymentApis from "../../api/feesAndPaymentApis";
import { formatDateTime } from "../../util/functions/UniversalFunctions";
import Tag from "../../components/Tags";
import { toast } from "sonner";
import { PageHeader } from "../../components";

export default function ViewInvoicePage() {
  const { id } = useParams();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      feesAndPaymentApis
        .getInvoiceById(Number(id))
        .then((res) => {
          if (res.data.status === 1) {
            setData(res.data.data);
          }
        })
        .finally(() => setLoading(false));
    }
  }, [id]);

  if (loading)
    return <div className="p-6 text-neutral-400">Loading...</div>;
  if (!data)
    return <div className="p-6 text-error-500">No Data Found</div>;

  const balance =
    (Number(data.amount) || 0) - (Number(data.amountReceived) || 0);

  const statusColor: any = {
    DRAFT: "bg-neutral-100 text-neutral-500",
    PENDING: "bg-warning-50 text-warning-600",
    PAID: "bg-success-50 text-success-600",
    PARTIAL: "bg-warning-100 text-warning-700",
  };

  return (
    <div className="space-y-6 min-h-screen">
      <PageHeader showBack title={`Invoice #${data.invoiceId}`} />
      <div className="bg-white p-6 rounded-2xl border border-background-300 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-700">
            {data.paymentTitle}
          </h1>
        </div>

        <span
          className={`px-3 py-1 rounded-full text-xs font-medium ${statusColor[data.paymentStatus]}`}
        >
          {data.paymentStatus}
        </span>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-white p-5 rounded-2xl border border-background-300">
          <p className="text-neutral-300 text-sm">Total Amount</p>
          <p className="text-2xl font-semibold text-primary-600 mt-2">
            ₹ {data.amount}
          </p>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-background-300">
          <p className="text-neutral-300 text-sm">Received</p>
          <p className="text-2xl font-semibold text-success-600 mt-2">
            ₹ {data.amountReceived ?? 0}
          </p>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-background-300">
          <p className="text-neutral-300 text-sm">Balance</p>
          <p className="text-2xl font-semibold text-error-500 mt-2">
            ₹ {balance}
          </p>
        </div>
      </div>

      <div className="bg-white p-6 rounded-2xl border border-background-300 grid md:grid-cols-2 gap-6">
        <div>
          <p className="text-neutral-300 text-sm">Due Date</p>
          <p className="text-neutral-600 font-medium mt-1">
            {formatDateTime(data.dueDate)}
          </p>
        </div>

        <div>
          <p className="text-neutral-300 text-sm">Created At</p>
          <p className="text-neutral-600 font-medium mt-1">
            {formatDateTime(data.createdAt)}
          </p>
        </div>
      </div>

      <div className="bg-white p-6 rounded-2xl border border-background-300">
        <h2 className="text-lg font-semibold text-neutral-700 mb-4">
          Invoice Items
        </h2>

        <div className="divide-y divide-background-300">
          {data.invoiceItems.map((item: any) => (
            <div
              key={item.itemId}
              className="py-4 flex justify-between items-center"
            >
              <div>
                <p className="font-medium text-neutral-600">
                  {item.itemTitle}
                </p>
                <p className="text-xs text-neutral-300 mt-1">
                  Base: ₹ {item.baseAmount} • Discount: ₹ {item.discount}
                </p>
              </div>

              <p className="font-semibold text-neutral-700">
                ₹ {item.finalAmount}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
